import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAlert } from 'react-alert';
import { Form, InputGroup, Table } from 'react-bootstrap';
import MetaData from '../../layouts/MetaData';
import Loader from '../../layouts/Loader';
import RoleShell from '../../components/Enrollment/RoleShell';
import { searchOnEnter, searchOrReset } from '../../components/searchOnEnter';
import { Prod01 } from '../../constants/ActionType';
import { useSortableTable, SortableTh, serverSortParams } from '../../components/SortableTable';

const ENROLLMENT_DEVICE_SORT_COLUMNS = { deviceId: {}, imei: {}, deviceType: {}, model: {} };

const LIMIT = 25;

// Read-only device inventory (own organizations): search by IMEI, device ID / serial, or assigned patient name.
const EnrollmentDevices = () => {
    const alert = useAlert();
    const [by, setBy] = useState('imei');
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState({ by: 'imei', search: '' });
    const [status, setStatus] = useState('all');
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ devices: [], count: 0 });
    const [loading, setLoading] = useState(false);
    const tableSort = useSortableTable(data.devices, { id: 'enrollment.devices', columns: ENROLLMENT_DEVICE_SORT_COLUMNS, manual: true });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${Prod01}/inventory/devices`, { params: { ...query, status, page, limit: LIMIT, ...(serverSortParams(tableSort, Object.keys(ENROLLMENT_DEVICE_SORT_COLUMNS)) || {}) } });
            setData(res.data);
        } catch (err) {
            alert.error(err?.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, [query, status, page, tableSort.sortKey, tableSort.sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        load();
    }, [load]);

    const runSearch = (v) => {
        setPage(1);
        setQuery({ by, search: String(v || '').trim() });
    };
    const reset = () => {
        setSearch('');
        runSearch('');
    };
    const pages = Math.max(1, Math.ceil((data.count || 0) / LIMIT));

    return (
        <>
            <MetaData title="Device Inventory" />
            <RoleShell title="Device Inventory">
                <h5 style={{ color: '#004aad' }}>
                    <i className="bx bxs-devices"></i> <strong>&nbsp;Device Inventory</strong>
                </h5>
                <hr />
                <div className="row-display mb-3" style={{ gap: 10 }}>
                    <select className="form-control shadow-none select-input-type" value={by} onChange={(e) => setBy(e.target.value)}>
                        <option value="imei">IMEI</option>
                        <option value="serial">Device ID / Serial</option>
                        <option value="patient">Assigned Patient Name</option>
                    </select>
                    <InputGroup>
                        <Form.Control
                            placeholder="Search here ..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={searchOnEnter(runSearch, reset)}
                        />
                        <button className="btn btn-outline-primary btn-sm" type="button" onClick={() => searchOrReset(search, runSearch, reset)}>
                            Search
                        </button>
                    </InputGroup>
                    <select className="form-control shadow-none select-input-type" value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }}>
                        <option value="all">All devices</option>
                        <option value="stock">In Stock</option>
                        <option value="assigned">Assigned</option>
                    </select>
                </div>
                {loading ? <Loader /> : (
                    <Table className="table table-sm table-striped">
                        <thead align="center">
                            <tr><SortableTh sortKey="deviceId" sort={tableSort}>Device ID</SortableTh><SortableTh sortKey="imei" sort={tableSort}>IMEI</SortableTh>
                                <SortableTh sortKey="deviceType" sort={tableSort}>Type</SortableTh><SortableTh sortKey="model" sort={tableSort}>Model</SortableTh><th>Status</th><th>Assigned To</th></tr>
                        </thead>
                        <tbody>
                            {data.devices.map((d) => (
                                <tr align="center" key={d._id}>
                                    <td>{d._id}</td>
                                    <td>{d.imei}</td>
                                    <td>{d.deviceType}</td>
                                    <td>{d.modelNumber || ''}</td>
                                    <td>{d.broken ? <span style={{ color: 'red' }}>Broken</span> : d.assigned_patient_id ? 'Assigned' : 'In Stock'}</td>
                                    <td>{d.assigned_patient_id ? `${d.assigned_patient_id.lastname}, ${d.assigned_patient_id.firstname}` : 'N/A'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                )}
                <div className="d-flex justify-content-between">
                    <small className="text-muted">{data.count || 0} devices</small>
                    <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm btn-outline-secondary" type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>⟨</button>
                        <small className="mt-1">{page} / {pages}</small>
                        <button className="btn btn-sm btn-outline-secondary" type="button" disabled={page >= pages} onClick={() => setPage(page + 1)}>⟩</button>
                    </div>
                </div>
            </RoleShell>
        </>
    );
};

export default EnrollmentDevices;
